import { create } from 'zustand';
import { persist, createJSONStorage } from 'zustand/middleware';

export type Theme = 'light' | 'dark' | 'system';

interface ThemeState {
  theme: Theme;
  resolvedTheme: 'light' | 'dark';
  isHydrated: boolean;
  setTheme: (theme: Theme) => void;
  toggleTheme: () => void;
  syncWithSystem: () => void;
}

// Storage key constant for consistency
const THEME_STORAGE_KEY = 'theme-storage';

// Get system preference
export const getSystemTheme = (): 'light' | 'dark' => {
  if (typeof window === 'undefined') return 'light';
  return window.matchMedia('(prefers-color-scheme: dark)').matches ? 'dark' : 'light';
};

// Resolve theme based on user preference and system preference
export const resolveTheme = (theme: Theme): 'light' | 'dark' => {
  if (theme === 'system') {
    return getSystemTheme();
  }
  return theme;
};

// Get initial theme from localStorage or system preference (for SSR/flash prevention)
const getInitialTheme = (): Theme => {
  if (typeof window === 'undefined') return 'system';

  try {
    const stored = localStorage.getItem(THEME_STORAGE_KEY);
    if (stored) {
      const parsed = JSON.parse(stored);
      if (parsed?.state?.theme) {
        return parsed.state.theme as Theme;
      }
    }
  } catch {
    // Ignore parsing errors
  }

  return 'system';
};

// Apply theme to document with optional transition control
export const applyTheme = (theme: 'light' | 'dark', enableTransition = true) => {
  if (typeof window === 'undefined') return;

  const root = window.document.documentElement;
  const body = window.document.body;

  // Disable transitions during initial load or theme switch to prevent flash
  if (!enableTransition) {
    root.classList.add('theme-transition-none');
    body.classList.add('theme-transition-none');
  }

  // Update html element classes
  root.classList.remove('light', 'dark');
  root.classList.add(theme);

  // Update body classes for mm-workplace/mm-light theming
  body.classList.remove('mm-workplace', 'mm-light', 'mm-workplace-bg', 'mm-light-bg');
  if (theme === 'dark') {
    body.classList.add('mm-workplace', 'mm-workplace-bg');
  } else {
    body.classList.add('mm-light', 'mm-light-bg');
  }

  // Update meta theme-color for mobile browsers
  const metaThemeColor = document.querySelector('meta[name="theme-color"]');
  if (metaThemeColor) {
    metaThemeColor.setAttribute('content', theme === 'dark' ? '#1a1a2e' : '#f8fafc');
  }

  // Re-enable transitions after a brief delay
  if (!enableTransition) {
    requestAnimationFrame(() => {
      requestAnimationFrame(() => {
        root.classList.remove('theme-transition-none');
        body.classList.remove('theme-transition-none');
      });
    });
  }
};

// Store the mediaQuery listener reference for cleanup
let mediaQueryListener: ((e: MediaQueryListEvent) => void) | null = null;
let isListenerSetup = false;

// Setup system theme change listener
const setupSystemThemeListener = (get: () => ThemeState, set: (partial: Partial<ThemeState>) => void) => {
  if (typeof window === 'undefined' || isListenerSetup) return;

  const mediaQuery = window.matchMedia('(prefers-color-scheme: dark)');

  const handleChange = (e: MediaQueryListEvent | MediaQueryList) => {
    const { theme } = get();
    if (theme === 'system') {
      const resolved = e.matches ? 'dark' : 'light';
      set({ resolvedTheme: resolved });
      applyTheme(resolved, true);
    }
  };

  // Store the listener reference for cleanup
  mediaQueryListener = handleChange as (e: MediaQueryListEvent) => void;
  isListenerSetup = true;

  // Modern browsers
  if (mediaQuery.addEventListener) {
    mediaQuery.addEventListener('change', handleChange);
  } else {
    // Legacy browsers
    mediaQuery.addListener(handleChange);
  }
};

export const useThemeStore = create<ThemeState>()(
  persist(
    (set, get) => {
      // Get initial theme to prevent flash
      const initialTheme = getInitialTheme();
      const initialResolved = resolveTheme(initialTheme);

      // Apply theme immediately on store creation (before hydration)
      if (typeof window !== 'undefined') {
        applyTheme(initialResolved, false);
      }

      return {
        theme: initialTheme,
        resolvedTheme: initialResolved,
        isHydrated: false,

        setTheme: (theme: Theme) => {
          const resolved = resolveTheme(theme);
          set({ theme, resolvedTheme: resolved });
          applyTheme(resolved, true);
        },

        toggleTheme: () => {
          const { theme, resolvedTheme } = get();
          // If in system mode, switch to the opposite of current resolved theme
          // Otherwise toggle between light and dark
          const newTheme: Theme = resolvedTheme === 'light' ? 'dark' : 'light';
          set({ theme: newTheme, resolvedTheme: newTheme });
          applyTheme(newTheme, true);
        },

        syncWithSystem: () => {
          const { theme } = get();
          if (theme === 'system') {
            const resolved = getSystemTheme();
            set({ resolvedTheme: resolved });
            applyTheme(resolved, true);
          }
        },
      };
    },
    {
      name: THEME_STORAGE_KEY,
      storage: createJSONStorage(() => localStorage),
      partialize: (state) => ({
        theme: state.theme,
        // Don't persist resolvedTheme - it should be computed
      }),
      onRehydrateStorage: () => (state, error) => {
        if (error) {
          console.warn('Theme store hydration error:', error);
          return;
        }

        if (state) {
          // Recalculate resolved theme after hydration
          const resolved = resolveTheme(state.theme);
          state.resolvedTheme = resolved;
          state.isHydrated = true;

          // Apply theme without transitions to prevent flash of unstyled content
          applyTheme(resolved, false);

          // Setup system theme listener after hydration
          setupSystemThemeListener(
            () => useThemeStore.getState(),
            (partial) => useThemeStore.setState(partial)
          );
        }
      },
    }
  )
);

// Initialize listener on first import (for cases where hydration doesn't trigger)
if (typeof window !== 'undefined') {
  // Delay setup to ensure store is fully initialized; only run if already hydrated
  // to avoid double listener setup (hydration callback also calls setupSystemThemeListener)
  setTimeout(() => {
    if (useThemeStore.persist.hasHydrated()) {
      setupSystemThemeListener(
        () => useThemeStore.getState(),
        (partial) => useThemeStore.setState(partial)
      );
    }
  }, 0);
}

// Cleanup function to remove event listener
export const cleanupThemeStore = () => {
  if (typeof window !== 'undefined' && mediaQueryListener) {
    const mediaQuery = window.matchMedia('(prefers-color-scheme: dark)');
    if (mediaQuery.removeEventListener) {
      mediaQuery.removeEventListener('change', mediaQueryListener);
    } else {
      mediaQuery.removeListener(mediaQueryListener);
    }
    mediaQueryListener = null;
    isListenerSetup = false;
  }
};

// Utility to check if theme is ready (hydrated)
export const isThemeReady = () => useThemeStore.getState().isHydrated;
